import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Pencil, Check, Plus } from 'lucide-react';
import { useApp } from '@/store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SignaturePad } from '@/components/SignaturePad';
import { PhotoUploader } from '@/components/PhotoUploader';
import { signAndPut, UploadKind, UploadFailedError } from '@/lib/uploads';
import { isPartCompatible } from '@/lib/spare-parts';
import { getDecoded } from '@/lib/auth';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { useT, HOSE_COND_LABEL_KEYS } from '@/lib/i18n';
import {
  COND_KEYS,
  conditionOptions,
  CONDITION_LABEL_KEYS,
  isConditionFault,
} from '@/lib/hose-condition';
import { contactRoleLabel } from '@/components/dialogs/ContactDialog';
import {
  Billing,
  ConditionValue,
  SparePartType,
  VisitStatus,
} from '@/data/types';
import type {
  Address,
  HoseCondition,
  InspectionDraft,
  InspectionReading,
  Visit,
} from '@/data/types';

interface Props {
  visit: Visit | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One-line postal address for the on-site header block. */
// Which billing classes survive into a new visit's draft.
//
// DISPOSED and NEW describe what the device IS — it has been taken away, or it
// has just been installed. Those states belong to the device and must not be
// cleared by opening a maintenance. MAINTENANCE is a decision about the
// current visit and is deliberately re-asked each time (client #67).
function carriedBilling(current?: Billing): Billing {
  return current === Billing.DISPOSED || current === Billing.NEW
    ? current
    : Billing.NONE;
}

function fmtAddress(a: Address): string {
  return [a.company, a.street, [a.zip, a.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

function defaultCondition(): HoseCondition {
  return {
    fireCock: ConditionValue.OK,
    seals: ConditionValue.OK,
    hose: ConditionValue.OK,
    nozzle: ConditionValue.OK,
    axle: ConditionValue.OK,
    swivelArm: ConditionValue.OK,
    doorLock: ConditionValue.OK,
    water: ConditionValue.OK,
  };
}

type Reading = Omit<InspectionReading, 'deviceId' | 'kind'>;
interface Dev {
  id: string;
  kind: 'extinguisher' | 'hose';
  number?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  year?: number;
  type?: string;
  length?: number;
  diameter?: string;
}

export function CompleteInspectionDialog({ visit, open, onOpenChange }: Props) {
  const t = useT();
  const customers = useApp(s => s.customers);
  const spareParts = useApp(s => s.spareParts);
  const manufacturers = useApp(s => s.manufacturers);
  const completeVisit = useApp(s => s.completeVisit);
  const upsertExtinguisher = useApp(s => s.upsertExtinguisher);
  const upsertHose = useApp(s => s.upsertHose);
  const toast = useApp(s => s.toast);
  // Device rights still apply inside a maintenance (#95): correcting a
  // placement is a device edit, adding one a device create.
  const rights = useSectionRights();
  const canEditDevice = (kind: Dev['kind']) =>
    rights[kind === 'extinguisher' ? SectionId.EXTINGUISHERS : SectionId.HOSES]
      .edit;

  const customer = customers.find(c => c.id === visit?.customerId);

  // Correct a device's placement in-line during maintenance (client 16 July —
  // "edit devices when in maintenance if something is incorrect").
  const patchDevice = (
    dev: Dev,
    patch: { floor?: string; location?: string }
  ) => {
    if (!customer) return;
    if (dev.kind === 'extinguisher') {
      const e = customer.extinguishers.find(x => x.id === dev.id);
      if (e) upsertExtinguisher(customer.id, { ...e, ...patch });
    } else {
      const h = customer.hoses.find(x => x.id === dev.id);
      if (h) upsertHose(customer.id, { ...h, ...patch });
    }
  };
  const devices: Dev[] = useMemo(() => {
    const ex = (customer?.extinguishers ?? []).map(e => ({
      id: e.id,
      kind: 'extinguisher' as const,
      number: e.deviceNumber,
      floor: e.floor,
      location: e.location,
      mfr: e.mfr,
      model: e.model,
      year: e.year,
      type: e.type,
    }));
    const ho = (customer?.hoses ?? []).map(h => ({
      id: h.id,
      kind: 'hose' as const,
      number: h.deviceNumber,
      floor: h.floor,
      location: h.location,
      mfr: h.mfr,
      model: h.model,
      year: h.year,
      length: h.length,
      diameter: h.diameter,
    }));
    return [...ex, ...ho];
  }, [customer]);

  // Readings live in the store, not here: the technician routinely leaves this
  // dialog mid-visit (adding a device navigates away, the modal can be
  // dismissed, a tablet can reload) and none of that may cost them their work.
  // `editingId`/`sig` stay local — they are navigation state, not field data.
  const visitId = visit?.id ?? null;
  const draft = useApp(s =>
    visitId ? s.inspectionDrafts[visitId] : undefined
  );
  const saveInspectionDraft = useApp(s => s.saveInspectionDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sig, setSig] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);

  const readings: Record<string, Reading> = useMemo(
    () => draft?.readings ?? {},
    [draft]
  );
  const unchecked = useMemo(
    () => new Set(draft?.unchecked ?? []),
    [draft?.unchecked]
  );

  // Seed the draft when the visit's dialog first opens, and top it up with
  // devices added since — never overwriting a reading already taken.
  useEffect(() => {
    if (!open || !customer || !visitId) return;
    // A submitted visit owns an Inspection now; never re-seed a draft for it.
    const stored = useApp.getState().visits.find(x => x.id === visitId);
    if (stored?.status === VisitStatus.COMPLETED) return;
    const prev = useApp.getState().inspectionDrafts[visitId];
    const next: Record<string, Reading> = { ...(prev?.readings ?? {}) };
    let changed = !prev;
    // Every device starts unticked: nothing is billed until the technician
    // actively ticks it, so a 200-device site cannot invoice work that was
    // never done (client #67). Hoses joined this rule with client #91 — they
    // were previously ticked by default. Only unticked devices are protected;
    // the tick is the deliberate act, and `completeVisit` never touches a
    // device that has no reading.
    //
    // "Maintenance" is a judgement about THIS visit, so it resets every time —
    // a device billed last year must be classified again, not silently
    // re-billed. But "disposed" and "new" are not per-visit judgements, they
    // are states the device is already in: the unit has physically gone, or it
    // has just been installed. Resetting those wiped a decision someone had
    // already made — dispose an extinguisher, tick it in the maintenance that
    // follows, and the disposal silently reverted to "no entry" and never
    // reached the invoice, which is the exact loss client #54 exists to
    // prevent. So lifecycle states carry over; the per-visit class does not.
    const unchecked = new Set(prev?.unchecked ?? []);
    customer.extinguishers.forEach(e => {
      if (next[e.id]) return;
      next[e.id] = {
        extCondition: ConditionValue.OK,
        // "Refilled this visit" is about THIS visit — seeding it from the
        // device's last refill date left it ticked on every later visit
        // (client #61). The stored date is kept; only the tick resets.
        refilled: '',
        spareParts: e.spareParts ?? [],
        photos: e.photos ?? [],
        billing: carriedBilling(e.billing),
        note: '',
      };
      unchecked.add(e.id);
      changed = true;
    });
    customer.hoses.forEach(h => {
      if (next[h.id]) return;
      next[h.id] = {
        restingPressure: h.restingPressure ?? '',
        flowPressure: h.flowPressure ?? '',
        flowRate: h.flowRate ?? '',
        condition: h.condition ?? defaultCondition(),
        photos: h.photos ?? [],
        billing: carriedBilling(h.billing),
      };
      unchecked.add(h.id);
      changed = true;
    });
    if (!changed) return;
    saveInspectionDraft(visitId, { readings: next, unchecked: [...unchecked] });
  }, [open, visitId, customer, saveInspectionDraft]);

  const updateDraft = (fn: (d: InspectionDraft) => InspectionDraft) => {
    if (!visitId) return;
    const cur = useApp.getState().inspectionDrafts[visitId] ?? {
      readings: {},
      unchecked: [],
    };
    saveInspectionDraft(visitId, fn(cur));
  };
  const patchReading = (id: string, fn: (r: Reading) => Reading) =>
    updateDraft(d => ({
      ...d,
      readings: { ...d.readings, [id]: fn(d.readings[id] ?? {}) },
    }));

  const patch = (id: string, p: Partial<Reading>) =>
    patchReading(id, r => ({ ...r, ...p }));
  const setCond = (id: string, key: keyof HoseCondition, v: ConditionValue) =>
    patchReading(id, r => ({
      ...r,
      condition: { ...(r.condition ?? defaultCondition()), [key]: v },
    }));
  const setCondNote = (id: string, key: keyof HoseCondition, v: string) =>
    patchReading(id, r => ({
      ...r,
      conditionNotes: { ...(r.conditionNotes ?? {}), [key]: v },
    }));
  const togglePart = (id: string, articleNr: string) =>
    patchReading(id, r => {
      const cur = r.spareParts ?? [];
      return {
        ...r,
        spareParts: cur.includes(articleNr)
          ? cur.filter(a => a !== articleNr)
          : [...cur, articleNr],
      };
    });
  // Classifying a device is what marks it as serviced — picking maintenance,
  // new or disposed ticks it, going back to "no entry" unticks it (client #67).
  const setBilling = (id: string, v: Billing) =>
    updateDraft(d => ({
      readings: {
        ...d.readings,
        [id]: { ...(d.readings[id] ?? {}), billing: v },
      },
      unchecked:
        v === Billing.NONE
          ? [...d.unchecked.filter(x => x !== id), id]
          : d.unchecked.filter(x => x !== id),
    }));
  const toggleServiced = (id: string, serviced: boolean) =>
    updateDraft(d => ({
      ...d,
      unchecked: serviced
        ? d.unchecked.filter(x => x !== id)
        : [...d.unchecked.filter(x => x !== id), id],
    }));

  // Both device kinds consume spare parts (client #17 — hoses were missing the
  // picker entirely). Extinguisher parts are narrowed to the device's model;
  // hose parts carry no model mapping, so the whole hose catalogue applies.
  const partsFor = (dev: Dev) =>
    spareParts.filter(p =>
      dev.kind === 'hose'
        ? p.type === SparePartType.HOSE
        : p.type === SparePartType.EXTINGUISHER &&
          isPartCompatible(p, { mfr: dev.mfr, model: dev.model }, manufacturers)
    );

  const sparePartPicker = (dev: Dev, selected: string[]) => (
    <div>
      <Label className="text-xs">{t('ext.spareParts')}</Label>
      <div className="max-h-32 overflow-auto border rounded-md p-2 space-y-1">
        {partsFor(dev).map(p => (
          <label key={p.articleNr} className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={selected.includes(p.articleNr)}
              onCheckedChange={() => togglePart(dev.id, p.articleNr)}
            />
            <span className="font-mono text-muted-foreground">
              {p.articleNr}
            </span>
            {p.name}
          </label>
        ))}
      </div>
    </div>
  );

  const today = () => new Date().toISOString().slice(0, 10);
  const condSelect = (
    key: keyof HoseCondition | null,
    value: ConditionValue,
    onChange: (v: ConditionValue) => void
  ) => (
    <Select value={value} onValueChange={v => onChange(v as ConditionValue)}>
      <SelectTrigger className="h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {/* Per-check findings — a dripping fire cock, rusty or sandy water
            (client #97). */}
        {conditionOptions(key, value).map(v => (
          <SelectItem key={v} value={v}>
            {t(CONDITION_LABEL_KEYS[v])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const submit = async () => {
    if (!visit) return;
    setBusy(true);
    try {
      let signatureKey: string | undefined;
      if (sig) {
        const file = new File([sig], 'signature.png', { type: 'image/png' });
        // Never lose a completed inspection because the signature upload
        // failed: warn and submit the visit without it (client #48).
        try {
          signatureKey = await signAndPut(file, UploadKind.SIGNATURE);
        } catch (err) {
          toast(
            err instanceof UploadFailedError
              ? t(err.i18nKey)
              : t('common.error')
          );
        }
      }
      const d = getDecoded();
      const tech = d?.email ?? d?.sub ?? '';
      const serviced = devices.filter(dev => !unchecked.has(dev.id));
      const readingList: InspectionReading[] = serviced.map(dev => ({
        deviceId: dev.id,
        kind: dev.kind,
        ...readings[dev.id],
      }));
      const done = await completeVisit(visit.id, {
        devicesServiced: serviced.map(dev => dev.id),
        readings: readingList,
        technician: tech,
        date: today(),
        signatureKey,
      });
      // A blocked completion has already said why; keep the work open.
      if (!done) return;
      toast(t('inspection.completed'));
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const editing = devices.find(d => d.id === editingId);
  const editingIdx = editing ? devices.findIndex(d => d.id === editing.id) : -1;
  const r = editingId ? (readings[editingId] ?? {}) : {};
  // Photo documentation is on-demand: only surfaced when a defect is flagged
  // this visit (client: photograph only when something is wrong).
  const hasDefect =
    editing?.kind === 'hose'
      ? COND_KEYS.some(k => isConditionFault(r.condition?.[k]))
      : r.extCondition === ConditionValue.NOT_OK;
  const ro = (label: string, value: unknown) =>
    value === undefined || value === null || value === '' ? null : (
      <div className="flex justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span>{String(value)}</span>
      </div>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A stray tap on the backdrop must not end a maintenance in progress —
          this modal is closed deliberately, via Cancel, the ✕, or Submit. */}
      <DialogContent
        className="sm:max-w-2xl"
        onInteractOutside={e => e.preventDefault()}
      >
        {editing ? (
          // ── Per-device edit view ────────────────────────────────────
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2"
                  onClick={() => setEditingId(null)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-xs">{t('common.backToOverview')}</span>
                </Button>
                <span className="text-destructive font-mono shrink-0">
                  #{editing.number || editing.id}
                </span>
                {/* Long location names used to push the title past the modal
                    edge (client #58) — clamp instead of overflow. */}
                <span
                  className="text-muted-foreground text-sm min-w-0 truncate"
                  title={editing.location}
                >
                  {editing.location}
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 min-w-0">
              <div className="rounded-md border p-3 text-xs space-y-1">
                {ro(t('ext.manufacturer'), editing.mfr)}
                {ro(t('ext.model'), editing.model)}
                {ro(t('ext.year'), editing.year)}
                {ro(t('ext.type'), editing.type)}
                {ro(t('hoseReport.length'), editing.length)}
                {ro('Ø', editing.diameter)}
              </div>

              <div>
                <Label className="text-xs">{t('inspection.editDevice')}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    key={`floor-${editing.id}`}
                    disabled={!canEditDevice(editing.kind)}
                    defaultValue={editing.floor ?? ''}
                    onBlur={e =>
                      patchDevice(editing, { floor: e.target.value })
                    }
                    placeholder={t('ext.floor')}
                    className="h-8"
                  />
                  <Input
                    key={`loc-${editing.id}`}
                    disabled={!canEditDevice(editing.kind)}
                    defaultValue={editing.location ?? ''}
                    onBlur={e =>
                      patchDevice(editing, { location: e.target.value })
                    }
                    placeholder={t('ext.location')}
                    className="h-8"
                  />
                </div>
              </div>

              {editing.kind === 'hose' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">
                        {t('hose.restingPressure')}
                      </Label>
                      <Input
                        data-testid="ins-resting"
                        value={r.restingPressure ?? ''}
                        onChange={e =>
                          patch(editing.id, { restingPressure: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">
                        {t('hose.flowPressure')}
                      </Label>
                      <Input
                        value={r.flowPressure ?? ''}
                        onChange={e =>
                          patch(editing.id, { flowPressure: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('hose.flowRate')}</Label>
                      <Input
                        value={r.flowRate ?? ''}
                        onChange={e =>
                          patch(editing.id, { flowRate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {COND_KEYS.map(k => (
                      <div key={k}>
                        <Label className="text-xs">
                          {t(HOSE_COND_LABEL_KEYS[k])}
                        </Label>
                        {condSelect(
                          k,
                          r.condition?.[k] ?? ConditionValue.OK,
                          v => setCond(editing.id, k, v)
                        )}
                      </div>
                    ))}
                  </div>
                  {COND_KEYS.filter(k =>
                    isConditionFault(r.condition?.[k])
                  ).map(k => (
                    <div key={`note-${k}`}>
                      <Label className="text-xs">
                        {t(HOSE_COND_LABEL_KEYS[k])} —{' '}
                        {t(
                          CONDITION_LABEL_KEYS[
                            r.condition?.[k] ?? ConditionValue.NOT_OK
                          ]
                        )}
                      </Label>
                      <Input
                        value={r.conditionNotes?.[k] ?? ''}
                        onChange={e =>
                          setCondNote(editing.id, k, e.target.value)
                        }
                        placeholder={t('hose.defectNote')}
                        className="h-8"
                      />
                    </div>
                  ))}
                  {sparePartPicker(editing, r.spareParts ?? [])}
                </div>
              )}

              {/* Billing applies to both device kinds — a hose is charged
                  and ticked on the same rule as an extinguisher (client #91). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t('ext.billing')}</Label>
                  {/* Radios, not a dropdown: a technician classifies every
                          device on a site in turn and two taps each adds up
                          (client #67). */}
                  <RadioGroup
                    className="gap-1 pt-1"
                    value={r.billing ?? Billing.NONE}
                    onValueChange={v => setBilling(editing.id, v as Billing)}
                  >
                    {[
                      { v: Billing.NONE, l: t('ext.billingNone') },
                      {
                        v: Billing.MAINTENANCE,
                        l: t('ext.billingMaintenance'),
                      },
                      { v: Billing.NEW, l: t('ext.billingNew') },
                      { v: Billing.DISPOSED, l: t('ext.billingDisposed') },
                    ].map(o => (
                      <div key={o.v} className="flex items-center gap-2">
                        <RadioGroupItem
                          value={o.v}
                          id={`ins-bill-${editing.id}-${o.v}`}
                        />
                        <Label
                          htmlFor={`ins-bill-${editing.id}-${o.v}`}
                          className="text-xs font-normal"
                        >
                          {o.l}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              {editing.kind === 'extinguisher' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">
                        {t('inspection.condition')}
                      </Label>
                      {condSelect(
                        null,
                        r.extCondition ?? ConditionValue.OK,
                        v => patch(editing.id, { extCondition: v })
                      )}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid="ins-refilled"
                      checked={!!r.refilled}
                      onCheckedChange={c =>
                        patch(editing.id, {
                          refilled: c === true ? today() : '',
                        })
                      }
                    />
                    {t('inspection.refilled')}
                  </label>
                  {sparePartPicker(editing, r.spareParts ?? [])}
                  <div>
                    <Label className="text-xs">{t('inspection.note')}</Label>
                    <Input
                      value={r.note ?? ''}
                      onChange={e =>
                        patch(editing.id, { note: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}

              {(hasDefect || (r.photos ?? []).length > 0) && (
                <div>
                  <Label className="text-xs">{t('photos.title')}</Label>
                  <PhotoUploader
                    value={r.photos ?? []}
                    onChange={next => patch(editing.id, { photos: next })}
                  />
                </div>
              )}

              <div className="flex justify-between gap-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={editingIdx <= 0}
                    onClick={() =>
                      setEditingId(devices[editingIdx - 1]?.id ?? null)
                    }
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={
                      editingIdx < 0 || editingIdx >= devices.length - 1
                    }
                    onClick={() =>
                      setEditingId(devices[editingIdx + 1]?.id ?? null)
                    }
                  >
                    {t('common.next')}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button onClick={() => setEditingId(null)}>
                  <Check className="h-4 w-4" />
                  {t('common.done')}
                </Button>
              </div>
            </div>
          </>
        ) : (
          // ── Device list view ────────────────────────────────────────
          <>
            <DialogHeader>
              <DialogTitle>{t('inspection.complete')}</DialogTitle>
            </DialogHeader>
            {/* min-w-0: DialogContent is a grid whose single implicit column
                defaults to min-width:auto, so a wide child (the two side-by-side
                address lines below) stretches the column past the dialog and
                pushes the per-device Prüfen button outside it (client #58). */}
            <div className="space-y-4 min-w-0">
              {customer && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
                  {/* Site and billing address side by side: the technician on
                      site can check both and phone the office if something is
                      off (client #69). */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {customer.objectAddress && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground">
                          {t('customer.objectAddress')}
                        </div>
                        <div>{fmtAddress(customer.objectAddress)}</div>
                      </div>
                    )}
                    {customer.billingAddress && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground">
                          {t('customer.billingAddress')}
                        </div>
                        <div>{fmtAddress(customer.billingAddress)}</div>
                      </div>
                    )}
                  </div>
                  {(customer.contacts ?? []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground">
                        {t('customer.contacts')}
                      </div>
                      {(customer.contacts ?? []).map((c, i) => (
                        <div key={i} className="text-muted-foreground">
                          {contactRoleLabel(c, t)}: {c.name}
                          {c.phone ? ` · ${c.phone}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  {customer.notes && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground">
                        {t('customer.notes')}
                      </div>
                      <div className="whitespace-pre-wrap text-muted-foreground">
                        {customer.notes}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label>{t('inspection.devices')}</Label>
                  {customer && (
                    <div className="flex gap-2">
                      {rights[SectionId.EXTINGUISHERS].create && (
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/customer/${customer.id}/extinguisher/new`}
                          >
                            <Plus className="h-3 w-3" />
                            {t('ext.addExtinguisher')}
                          </Link>
                        </Button>
                      )}
                      {rights[SectionId.HOSES].create && (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/customer/${customer.id}/hose/new`}>
                            <Plus className="h-3 w-3" />
                            {t('hose.addHose')}
                          </Link>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {devices.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('inspection.noDevices')}
                  </p>
                )}
                {devices.map(dev => (
                  <div
                    key={dev.id}
                    className="flex items-center gap-3 rounded-md border p-2 text-sm min-w-0"
                  >
                    <Checkbox
                      data-testid={`ins-serviced-${dev.id}`}
                      checked={!unchecked.has(dev.id)}
                      onCheckedChange={c => toggleServiced(dev.id, c === true)}
                    />
                    <span className="font-mono font-semibold text-destructive shrink-0">
                      #{dev.number || dev.id}
                    </span>
                    {/* Clamping keeps the row inside the modal, but the full
                        location has to stay reachable — several sites here run
                        past 60 characters (client #58). */}
                    <span
                      className="flex-1 truncate text-muted-foreground"
                      title={`${dev.floor ? dev.floor + ' · ' : ''}${dev.location}`}
                    >
                      {dev.kind === 'hose'
                        ? t('inspection.hose')
                        : t('inspection.extinguisher')}{' '}
                      · {dev.floor ? dev.floor + ' · ' : ''}
                      {dev.location}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      data-testid={`ins-edit-${dev.id}`}
                      onClick={() => setEditingId(dev.id)}
                    >
                      <Pencil className="h-3 w-3" />
                      {t('inspection.inspect')}
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>{t('inspection.signature')}</Label>
                <SignaturePad onChange={setSig} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={submit} disabled={busy || !visit}>
                  {t('inspection.submit')}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
